import Header from 'common/components/Header/RevampHeader'
import Layout from 'common/components/Layout'
import MainFooter from 'common/components/MainFooter/RevampFooter'
import Typography from 'common/components/Typography'
import { isClientAuthorized } from 'lib/auth/authorize-client'
import { getCookie, setCookie } from 'lib/cookie'
import {
  CUSTOM_EVENTS,
  subscribeEvent,
  unsubscribeEvent,
} from 'lib/events'
import { useLoadedFlag } from 'lib/feature-flags'
import objectToCamelCase from 'lib/transform/object-to-camel-case'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CallToActionSection from 'services/Home/components/CallToActionSection'
import CategoriesExploration from 'services/Home/components/CategoryExploration'
import FAQComponent from 'services/Home/components/FAQComponent'
import ProductCategoriesSection from 'services/Home/components/ProductCategoriesSection'
import ProductRecommendation from 'services/Home/components/ProductRecommendation'
import SellerRecommendation from 'services/Home/components/SellerRecommendation'
import ServicesComponent from 'services/Home/components/Services'
import ServicesPromoSection from 'services/Home/components/ServicesPromoSection'
import Article from 'services/Home/screens/Home/Article'
import Banner from 'services/Home/screens/Home/Banner'
import DailyDeals from 'services/Home/screens/Home/DailyDeals/screens'
import MetaData from 'services/Home/screens/Home/MetaData'
import Partners from 'services/Home/screens/Home/Partners/screens'
import ProductHomepage from 'services/Home/screens/Home/ProductHomepage/screens'
import SmartAlert from 'services/Home/widgets/SmartAlert'
import ModalConfirmationCloseProfiling from 'services/Profile/widget/ModalConfirmationCloseProfiling'
import IdProfiling from 'services/Questionnaire/constants'
import ModalQuestionnaireWrapper from 'services/Questionnaire/widget/ModalQuestionnaireWrapper'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const TopSection = styled.section`
  background: ${(props) => props.theme.colors.grayLighter};
  padding: 24px 0 32px;

  & > div {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`

const CategoriesExplorationSection = styled.div`
  background: linear-gradient(
    180deg,
    ${(props) => props.theme.colors.grayLighter} 0.59%,
    #ffffff 35.98%
  );
`

function Home({ lang }: { lang?: string }): JSX.Element {
  const { t, i18n } = useTranslation('home')
  const [showModalProfileType, setShowModalProfileType] = useState(
    false,
  )
  const [isQuestionnaireOpened, setIsQuestionnaireOpened] = useState(
    false,
  )
  const [
    showConfirmationCloseProfileType,
    setShowConfirmationCloseProfileType,
  ] = useState<boolean>(false)
  const [
    popularProductPropData,
    setPopularProductPropData,
  ] = useState(null)
  const [faqPropData, setFaqPropData] = useState(null)

  const { data } = useStoreState((state) => state.questionnaire)
  const websiteUserProfilingGlobalFeature = useLoadedFlag(
    'biz--questionnaire--user-profiling--website',
  )
  const servicesPromoSectionFlag = useLoadedFlag(
    'biz-services-promo-section-enable-global',
  )

  const isLogin = isClientAuthorized()

  const {
    getFirstQuestionnaire,
    setQuestionnaireId,
  } = useStoreActions((actions) => actions.questionnaire)
  const { getRecommendations } = useStoreActions(
    (actions) => actions.recommendations,
  )
  const { getData: getBusinessSolutionData } = useStoreActions(
    (actions) => actions.businessSolution,
  )
  const businessSolutionState = useStoreState(
    (state) => state.businessSolution,
  )
  useEffect(() => {
    if (
      isLogin &&
      data?.length > 0 &&
      websiteUserProfilingGlobalFeature
    ) {
      return setShowModalProfileType(
        getCookie('user_profiling') !== 'false',
      )
    }
    setShowModalProfileType(false)
    return setQuestionnaireId('')
  }, [
    isLogin,
    data,
    setQuestionnaireId,
    websiteUserProfilingGlobalFeature,
  ])

  useEffect(() => {
    if (
      isLogin &&
      websiteUserProfilingGlobalFeature &&
      getCookie('user_profiling') !== 'false'
    ) {
      getFirstQuestionnaire(IdProfiling.profilingAccountType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin, websiteUserProfilingGlobalFeature])

  useEffect(() => {
    getRecommendations()
    getBusinessSolutionData({ page_code: 'ralali_homepage' })
  }, [getRecommendations, getBusinessSolutionData, i18n.language])

  const layoutProps = (): object => {
    return {
      header: { component: <Header lang={lang} /> },
      footer: { component: <MainFooter /> },
    }
  }

  useEffect(() => {
    const action = () => {
      setIsQuestionnaireOpened(true)
    }

    subscribeEvent(CUSTOM_EVENTS.ON_QUESTIONNAIRE_OPEN, action)

    return () => {
      unsubscribeEvent(CUSTOM_EVENTS.ON_QUESTIONNAIRE_OPEN, action)
    }
  })

  useEffect(() => {
    const action = () => {
      setIsQuestionnaireOpened(false)
    }

    subscribeEvent(CUSTOM_EVENTS.ON_QUESTIONNAIRE_CLOSE, action)

    return () => {
      unsubscribeEvent(CUSTOM_EVENTS.ON_QUESTIONNAIRE_CLOSE, action)
    }
  })

  const toggleQuestionnaireModal = () => {
    setShowModalProfileType(!showModalProfileType)
    setShowConfirmationCloseProfileType(
      !showConfirmationCloseProfileType,
    )
  }

  const handleCloseConfirmationModal = () => {
    setCookie(
      'user_profiling',
      'false',
      '/',
      new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000), // 7 days
    )
    setShowModalProfileType(false)
    setShowConfirmationCloseProfileType(false)
  }

  useEffect(() => {
    const mapper = {
      'popular-product': (popularProduct) => {
        if (popularProduct) {
          setPopularProductPropData(objectToCamelCase(popularProduct))
        }
      },
      'ralali-frequently-answer-question': setFaqPropData,
    }

    if (businessSolutionState?.data?.sections) {
      businessSolutionState.data.sections.forEach((section) => {
        if (mapper[section.code]) {
          mapper[section.code](section.data)
        }
      })
    }
  }, [businessSolutionState?.data?.sections])

  return (
    <Layout {...layoutProps()} data-testid="home-layout">
      <MetaData />
      <Typography type="bh1" display="none">
        {t('HOME_H1')}
      </Typography>
      <TopSection>
        <div>
          <SmartAlert page="home" />
          <Banner />
          <ServicesComponent />
          <ProductCategoriesSection
            categories={popularProductPropData?.categories}
          />
          <ProductRecommendation />
        </div>
      </TopSection>
      <CategoriesExplorationSection>
        <CategoriesExploration />
      </CategoriesExplorationSection>
      <DailyDeals />
      <ProductHomepage />
      {servicesPromoSectionFlag && <ServicesPromoSection />}
      <SellerRecommendation />
      <Partners />
      <Article />
      <FAQComponent data={faqPropData} />
      <CallToActionSection />

      {/* use this component below for activate account type */}
      {!isQuestionnaireOpened &&
        websiteUserProfilingGlobalFeature && (
          <ModalQuestionnaireWrapper
            isOpen={showModalProfileType}
            id={IdProfiling.profilingAccountType}
            onClose={toggleQuestionnaireModal}
          />
        )}
      <ModalConfirmationCloseProfiling
        showConfirmationCloseProfileType={
          showConfirmationCloseProfileType
        }
        toggleQuestionnaireModal={toggleQuestionnaireModal}
        handleCloseConfirmationModal={handleCloseConfirmationModal}
      />
      {/* <h1>{t('h1')}</h1> */}
      {/* <Link href="/" locale={router.locale === 'id' ? 'en' : 'id'}> */}
      {/*  <button type="button">{t('change-locale')}</button> */}
      {/* </Link> */}
      {/* <Link href="/services"> */}
      {/*  <button type="button">{t('to-service-page')}</button> */}
      {/* </Link> */}
    </Layout>
  )
}

export default Home
